import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLocalization } from '../../contexts/LocalizationContext';
import contentSectionService from '../../services/contentSectionService';
import { formatDateTime } from '../../utils/formatters';
import Loading from '../../components/Common/Loading';
import Button from '../../components/Common/Button';
import Modal from '../../components/Common/Modal';
import MessageComponent from '../../components/Common/MessageComponent';
import PreviewGrid from './PreviewGrid';
import {
  AUTO_SORTS, SECTION_TYPES, STATUSES, ZONES, extractErrors, sectionTypeMeta, statusMeta, zoneMeta,
} from './constants';

const sortLabel = (v, isRtl) => {
  const s = AUTO_SORTS.find((x) => x.value === v);
  return s ? (isRtl ? s.ar : s.en) : '';
};

const ContentSectionsIndex = () => {
  const { isRtl } = useLocalization();
  const navigate = useNavigate();
  const tr = (en, ar) => (isRtl ? ar : en);
  const locale = isRtl ? 'ar' : 'en';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [toast, setToast] = useState({ visible: false, type: 'success', message: '' });
  const [toDelete, setToDelete] = useState(null);
  const [previewFor, setPreviewFor] = useState(null); // { section, data, loading, error }
  const [drag, setDrag] = useState(null); // { id, overId, overZone }

  const notify = (type, message) => setToast({ visible: true, type, message });

  const load = useCallback(async () => {
    try {
      const data = await contentSectionService.list({
        search: search.trim() || undefined,
        type: typeFilter === '' ? undefined : Number(typeFilter),
        status: statusFilter,
      });
      setItems(data || []);
    } catch (e) {
      notify('error', extractErrors(e, tr('Failed to load sections.', 'تعذر تحميل الأقسام.')).messages[0]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, typeFilter, statusFilter]);

  useEffect(() => {
    const t = setTimeout(load, search ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const filtering = Boolean(search.trim()) || typeFilter !== '' || statusFilter !== 'all';

  const byZone = useMemo(
    () => ZONES.map((z) => ({ zone: z, rows: items.filter((i) => i.zone === z.value) })),
    [items],
  );

  // ───────── drag & drop: one plain (zone, position) list, renumbered 1..n per zone ─────────
  const applyMove = async (dragId, targetZone, beforeId) => {
    const moving = items.find((i) => i.id === dragId);
    if (!moving) return;
    const rest = items.filter((i) => i.id !== dragId);
    const next = [];
    ZONES.forEach((z) => {
      const zoneRows = rest.filter((i) => i.zone === z.value);
      if (z.value === targetZone) {
        const idx = beforeId ? zoneRows.findIndex((i) => i.id === beforeId) : -1;
        const at = idx === -1 ? zoneRows.length : idx;
        zoneRows.splice(at, 0, { ...moving, zone: targetZone });
      }
      zoneRows.forEach((r, i) => next.push({ ...r, sortOrder: i + 1 }));
    });
    const before = items;
    setItems(next);
    try {
      await contentSectionService.reorder(next.map((r) => ({ sectionId: r.id, zone: r.zone, sortOrder: r.sortOrder })));
      notify('success', tr('Order saved. The website updates within a minute.', 'تم حفظ الترتيب. يظهر على الموقع خلال دقيقة.'));
    } catch (e) {
      setItems(before);
      notify('error', extractErrors(e, tr('Could not save the order.', 'تعذر حفظ الترتيب.')).messages[0]);
    }
  };

  const onDrop = (e, zone, beforeId) => {
    e.preventDefault();
    e.stopPropagation();
    const id = drag?.id;
    setDrag(null);
    if (id && id !== beforeId) applyMove(id, zone, beforeId);
  };

  // ───────── row actions ─────────
  const toggleActive = async (row) => {
    try {
      await contentSectionService.setActive(row.id, !row.isActive);
      await load();
    } catch (e) {
      notify('error', extractErrors(e, tr('Update failed.', 'فشل التحديث.')).messages[0]);
    }
  };

  const duplicate = async (row) => {
    try {
      const copy = await contentSectionService.duplicate(row.id);
      notify('success', tr('Duplicated as an inactive copy.', 'تم النسخ كنسخة غير مفعّلة.'));
      navigate(`/content-sections/${copy.id}`);
    } catch (e) {
      notify('error', extractErrors(e, tr('Duplicate failed.', 'فشل النسخ.')).messages[0]);
    }
  };

  const confirmDelete = async () => {
    const row = toDelete;
    setToDelete(null);
    try {
      await contentSectionService.remove(row.id);
      notify('success', tr('Section deleted.', 'تم حذف القسم.'));
      await load();
    } catch (e) {
      notify('error', extractErrors(e, tr('Delete failed.', 'فشل الحذف.')).messages[0]);
    }
  };

  const openPreview = async (row) => {
    setPreviewFor({ section: row, data: null, loading: true });
    try {
      const data = await contentSectionService.previewSaved(row.id);
      setPreviewFor({ section: row, data, loading: false });
    } catch (e) {
      setPreviewFor({ section: row, data: null, loading: false, error: extractErrors(e, 'Preview failed').messages[0] });
    }
  };

  const title = (r) => (isRtl ? r.titleAr || r.titleEn : r.titleEn);
  const schedule = (r) =>
    !r.startsAtUtc && !r.endsAtUtc
      ? tr('Always', 'دائماً')
      : `${r.startsAtUtc ? formatDateTime(r.startsAtUtc, locale) : '…'} → ${r.endsAtUtc ? formatDateTime(r.endsAtUtc, locale) : '…'}`;

  if (loading) return <Loading text={tr('Loading sections...', 'جاري تحميل الأقسام...')} />;

  return (
    <div className="cs-page">
      <MessageComponent
        message={toast.message}
        type={toast.type}
        isVisible={toast.visible}
        onClose={() => setToast((t) => ({ ...t, visible: false }))}
      />

      <div className="page-header">
        <div className="page-header-main">
          <h1 className="page-title">{tr('Content Sections', 'أقسام المحتوى')}</h1>
          <p className="page-description">
            {tr(
              'Build the homepage from product sections: pin products, auto-fill the rest, schedule and reorder — no code or redeploy needed.',
              'ابنِ الصفحة الرئيسية من أقسام المنتجات: ثبّت منتجات واملأ الباقي تلقائياً وجدوِل ورتّب — دون كود أو إعادة نشر.',
            )}
          </p>
        </div>
        <div className="page-header-actions">
          <Link to="/content-sections/new" className="btn btn-primary">+ {tr('New section', 'قسم جديد')}</Link>
        </div>
      </div>

      <div className="card cs-toolbar">
        <input
          className="form-input"
          type="search"
          placeholder={tr('Search by title or slug…', 'ابحث بالعنوان أو المعرّف…')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="form-select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="">{tr('All types', 'كل الأنواع')}</option>
          {SECTION_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{isRtl ? t.ar : t.en}</option>
          ))}
        </select>
        <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">{tr('All statuses', 'كل الحالات')}</option>
          {STATUSES.map((s) => (
            <option key={s.value} value={s.value}>{isRtl ? s.ar : s.en}</option>
          ))}
        </select>
      </div>

      {filtering ? (
        <div className="cs-hint cs-hint--accent">
          {tr('Clear the search and filters to reorder by drag and drop.', 'امسح البحث والتصفية لتتمكن من إعادة الترتيب بالسحب والإفلات.')}
        </div>
      ) : (
        <div className="cs-hint">
          {tr('Drag a row by its handle to reorder, or drop it into another position group. Order is saved immediately.', 'اسحب الصف من المقبض لإعادة الترتيب أو أفلته في موضع آخر. يُحفظ الترتيب فوراً.')}
        </div>
      )}

      {items.length === 0 ? (
        <div className="card empty-state">
          <div className="empty-title">{filtering ? tr('No sections match your filters', 'لا توجد أقسام مطابقة') : tr('No sections yet', 'لا توجد أقسام بعد')}</div>
          <p className="empty-description">{tr('Create your first section to show products on the homepage.', 'أنشئ أول قسم لعرض المنتجات في الصفحة الرئيسية.')}</p>
          {!filtering ? <Link to="/content-sections/new" className="btn btn-primary">+ {tr('New section', 'قسم جديد')}</Link> : null}
        </div>
      ) : (
        byZone.map(({ zone, rows }) => (
          <div
            key={zone.value}
            className={`cs-zone ${drag && drag.overZone === zone.value && !drag.overId ? 'is-drop' : ''}`}
            onDragOver={(e) => {
              if (filtering || !drag) return;
              e.preventDefault();
              setDrag((d) => (d ? { ...d, overZone: zone.value, overId: d.overZone === zone.value ? d.overId : null } : d));
            }}
            onDrop={(e) => !filtering && onDrop(e, zone.value, drag?.overZone === zone.value ? drag.overId : null)}
          >
            <div className="cs-zone-head">
              <h2>{isRtl ? zone.ar : zone.en}</h2>
              <span className="badge badge-neutral">{rows.length}</span>
            </div>

            {rows.length === 0 ? (
              <div className="cs-empty">{tr('No sections here.', 'لا توجد أقسام هنا.')}</div>
            ) : (
              <ul className="cs-rows">
                {rows.map((r) => {
                  const st = statusMeta(r.status);
                  const tm = sectionTypeMeta(r.sectionType);
                  return (
                    <li
                      key={r.id}
                      className={`cs-row ${drag?.id === r.id ? 'is-dragging' : ''} ${drag && drag.overId === r.id && drag.id !== r.id ? 'is-over' : ''}`}
                      draggable={!filtering}
                      onDragStart={(e) => {
                        setDrag({ id: r.id, overId: null, overZone: zone.value });
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', r.id);
                      }}
                      onDragOver={(e) => {
                        if (filtering || !drag) return;
                        e.preventDefault();
                        e.stopPropagation();
                        if (drag.overId !== r.id) setDrag({ ...drag, overId: r.id, overZone: zone.value });
                      }}
                      onDrop={(e) => !filtering && onDrop(e, zone.value, r.id)}
                      onDragEnd={() => setDrag(null)}
                    >
                      <span className={`cs-grip ${filtering ? 'is-disabled' : ''}`} aria-hidden="true">⋮⋮</span>
                      <span className="cs-order" title={tr('Display order', 'ترتيب العرض')}>{r.sortOrder}</span>

                      <div className="cs-row-main">
                        <Link to={`/content-sections/${r.id}`} className="cs-row-title">{title(r)}</Link>
                        <div className="cs-row-sub" dir="ltr">/catalog?section={r.slug}</div>
                      </div>

                      <div className="cs-row-cells">
                        <span className="badge badge-primary">{isRtl ? tm.ar : tm.en}</span>
                        <span className={`badge ${st.badge}`}>{isRtl ? st.ar : st.en}</span>
                        <span className="cs-cell" title={tr('Pinned products', 'منتجات مثبّتة')}>📌 {r.pinnedCount}</span>
                        <span className="cs-cell" title={tr('Auto-fill', 'تعبئة تلقائية')}>
                          {r.autoFill ? `${tr('Auto', 'تلقائي')} · ${sortLabel(r.autoSort, isRtl)}` : tr('Manual only', 'يدوي فقط')}
                        </span>
                        <span className="cs-cell" title={tr('Homepage limit', 'حد الصفحة الرئيسية')}>
                          {tr('Limit', 'الحد')} {r.homepageLimit}{r.showViewAll ? '' : ` · ${tr('no View all', 'بدون عرض الكل')}`}
                        </span>
                        <span className="cs-cell" title={tr('Position', 'الموضع')}>
                          {isRtl ? zoneMeta(r.zone).shortAr : zoneMeta(r.zone).shortEn} · #{r.sortOrder}
                        </span>
                        <span className="cs-cell" title={tr('Website', 'الموقع')}>
                          {r.websiteVisible ? tr('Website: on', 'الموقع: ظاهر') : tr('Website: off', 'الموقع: مخفي')}
                        </span>
                        <span className="cs-cell cs-cell--wide" title={tr('Schedule', 'الجدولة')}>🗓 {schedule(r)}</span>
                      </div>

                      <div className="cs-row-actions">
                        <button
                          type="button"
                          className={`cs-switch ${r.isActive ? 'is-on' : ''}`}
                          role="switch"
                          aria-checked={r.isActive}
                          aria-label={tr('Enable / disable', 'تفعيل / تعطيل')}
                          onClick={() => toggleActive(r)}
                        />
                        <Link to={`/content-sections/${r.id}`} className="btn btn-secondary btn-small">{tr('Edit', 'تعديل')}</Link>
                        <button type="button" className="btn btn-ghost btn-small" onClick={() => openPreview(r)}>{tr('Preview', 'معاينة')}</button>
                        <button type="button" className="btn btn-ghost btn-small" onClick={() => duplicate(r)}>{tr('Duplicate', 'نسخ')}</button>
                        <button type="button" className="btn btn-ghost btn-small cs-danger" onClick={() => setToDelete(r)}>{tr('Delete', 'حذف')}</button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        ))
      )}

      <Modal
        isOpen={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        title={tr('Delete section?', 'حذف القسم؟')}
        size="small"
        footer={(
          <>
            <Button variant="secondary" onClick={() => setToDelete(null)}>{tr('Cancel', 'إلغاء')}</Button>
            <Button variant="danger" onClick={confirmDelete}>{tr('Delete', 'حذف')}</Button>
          </>
        )}
      >
        <p>
          {tr('This removes', 'سيُزال')} <strong>{toDelete ? title(toDelete) : ''}</strong>{' '}
          {tr('from the website. Links to it will show a friendly "no longer available" notice. Products are not affected.', 'من الموقع. ستعرض الروابط القديمة له رسالة «لم يعد متاحاً». لا تتأثر المنتجات.')}
        </p>
      </Modal>

      <Modal
        isOpen={Boolean(previewFor)}
        onClose={() => setPreviewFor(null)}
        title={previewFor ? `${tr('Preview', 'معاينة')}: ${title(previewFor.section)}` : ''}
        size="xl"
      >
        {previewFor?.loading ? <Loading text={tr('Building preview...', 'جاري بناء المعاينة...')} /> : null}
        {previewFor?.error ? <div className="cs-alert">{previewFor.error}</div> : null}
        {previewFor?.data ? <PreviewGrid preview={previewFor.data} isRtl={isRtl} /> : null}
      </Modal>
    </div>
  );
};

export default ContentSectionsIndex;
